import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../../context/NotificationContext';
import axios from 'axios';
import { API_URL } from '../../../config/api';
import { getFullImageUrl } from '../../../utils/imageUtils';
import MediaSelector from '../../../components/MediaSelector';
import './LandingContent.css';

const LandingContent = () => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useNotification();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [heroLines, setHeroLines] = useState([]);
  const [news, setNews] = useState([]);
  const [advertisements, setAdvertisements] = useState([]);
  const [editingNews, setEditingNews] = useState(null);
  const [editingAd, setEditingAd] = useState(null);
  const [newsForm, setNewsForm] = useState({ title: '', body: '', date: new Date().toISOString().split('T')[0], order: 0 });
  const [adForm, setAdForm] = useState({ title: '', image: '', link: '', order: 0 });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/landing-content`);
      setHeroLines(Array.isArray(response.data.heroLines) ? response.data.heroLines : []);
      setNews(response.data.news || []);
      setAdvertisements(response.data.advertisements || []);
    } catch (error) {
      console.error('Error fetching landing content:', error);
      showError(t('admin.landingContentLoadError') || 'Failed to load landing content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/landing-content`, {
        heroLines: heroLines.map(s => (s || '').trim()).filter(Boolean),
        news: news.map((n, i) => ({ ...n, order: i })),
        advertisements: advertisements.map((a, i) => ({ ...a, order: i }))
      });
      showSuccess(t('admin.landingContentSaved') || 'Landing content saved');
      await fetchContent();
    } catch (error) {
      showError(error.response?.data?.message || (t('admin.landingContentSaveError') || 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const addHeroLine = () => setHeroLines(prev => [...prev, '']);
  const updateHeroLine = (index, value) => setHeroLines(prev => prev.map((s, i) => (i === index ? value : s)));
  const removeHeroLine = (index) => setHeroLines(prev => prev.filter((_, i) => i !== index));
  const moveHeroLine = (index, delta) => {
    const next = index + delta;
    if (next < 0 || next >= heroLines.length) return;
    setHeroLines(prev => {
      const arr = [...prev];
      [arr[index], arr[next]] = [arr[next], arr[index]];
      return arr;
    });
  };

  const openAddNews = () => {
    setEditingNews('new');
    setNewsForm({
      title: '',
      body: '',
      date: new Date().toISOString().split('T')[0],
      order: news.length
    });
  };

  const openEditNews = (item, index) => {
    setEditingNews(index);
    setNewsForm({
      title: item.title || '',
      body: item.body || '',
      date: item.date ? new Date(item.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      order: index
    });
  };

  const saveNewsItem = () => {
    const item = {
      title: newsForm.title.trim(),
      body: newsForm.body.trim(),
      date: new Date(newsForm.date).toISOString(),
      order: editingNews === 'new' ? news.length : editingNews
    };
    if (!item.title || !item.body) return;
    if (editingNews === 'new') {
      setNews(prev => [...prev, item]);
    } else {
      setNews(prev => prev.map((n, i) => (i === editingNews ? item : n)));
    }
    setEditingNews(null);
  };

  const deleteNews = (index) => {
    if (!window.confirm(t('admin.confirmDelete') || 'Delete this item?')) return;
    setNews(prev => prev.filter((_, i) => i !== index));
    setEditingNews(null);
  };

  const addAdFormRef = React.useRef(null);
  const openAddAd = () => {
    setEditingAd('new');
    setAdForm({
      title: '',
      image: '',
      link: '',
      order: advertisements.length
    });
    // Scroll the new form into view after it mounts
    setTimeout(() => addAdFormRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' }), 100);
  };

  const openEditAd = (item, index) => {
    setEditingAd(index);
    setAdForm({
      title: item.title || '',
      image: item.image || '',
      link: item.link || '',
      order: index
    });
  };

  const saveAdItem = () => {
    const item = {
      title: adForm.title.trim(),
      image: adForm.image.trim(),
      link: (adForm.link || '').trim(),
      order: editingAd === 'new' ? advertisements.length : editingAd
    };
    if (!item.title || !item.image) return;
    if (editingAd === 'new') {
      setAdvertisements(prev => [...prev, item]);
    } else {
      setAdvertisements(prev => prev.map((a, i) => (i === editingAd ? item : a)));
    }
    setEditingAd(null);
  };

  const deleteAd = (index) => {
    if (!window.confirm(t('admin.confirmDelete') || 'Delete this item?')) return;
    setAdvertisements(prev => prev.filter((_, i) => i !== index));
    setEditingAd(null);
  };

  const moveNews = (index, dir) => {
    const next = index + dir;
    if (next < 0 || next >= news.length) return;
    const arr = [...news];
    [arr[index], arr[next]] = [arr[next], arr[index]];
    setNews(arr);
  };

  const moveAd = (index, dir) => {
    const next = index + dir;
    if (next < 0 || next >= advertisements.length) return;
    const arr = [...advertisements];
    [arr[index], arr[next]] = [arr[next], arr[index]];
    setAdvertisements(arr);
  };

  if (loading) {
    return (
      <div className="landing-content-admin">
        <div className="loading-spinner">{t('admin.loading')}</div>
      </div>
    );
  }

  return (
    <div className="landing-content-admin">
      <div className="landing-content-header">
        <h2>{t('admin.landingContent') || 'Landing Page – News & Advertisements'}</h2>
        <button type="button" onClick={handleSave} className="btn-save-landing" disabled={saving}>
          {saving ? t('admin.saving') || 'Saving...' : t('admin.save')}
        </button>
      </div>

      <p className="landing-content-intro">
        {t('admin.landingContentIntro') || 'Edit the news and advertisements shown on the landing page. Click Save to publish changes.'}
      </p>

      {/* Hero / Changing text */}
      <section className="landing-content-section">
        <h3>{t('admin.heroLines') || 'Hero text (changing lines)'}</h3>
        <p className="landing-content-section-intro">{t('admin.heroLinesIntro') || 'One line is shown at a time on the landing page. Add multiple lines to rotate.'}</p>
        <button type="button" onClick={addHeroLine} className="btn-add-item">
          {t('admin.addHeroLine') || 'Add line'}
        </button>
        <div className="landing-content-list">
          {heroLines.length === 0 && (
            <p className="no-items">{t('admin.noNewsItems') || 'No lines. Add one above.'}</p>
          )}
          {heroLines.map((line, index) => (
            <div key={index} className="landing-content-list-item hero-line-item">
              <input
                type="text"
                value={line}
                onChange={e => updateHeroLine(index, e.target.value)}
                placeholder={`Line ${index + 1}`}
                className="hero-line-input"
              />
              <div className="item-actions">
                <button type="button" onClick={() => moveHeroLine(index, -1)} disabled={index === 0} title={t('admin.moveUp') || 'Move up'}>↑</button>
                <button type="button" onClick={() => moveHeroLine(index, 1)} disabled={index === heroLines.length - 1} title={t('admin.moveDown') || 'Move down'}>↓</button>
                <button type="button" onClick={() => removeHeroLine(index)} className="btn-delete">{t('admin.delete')}</button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* News Section */}
      <section className="landing-content-section">
        <h3>{t('landing.news') || 'News'}</h3>
        <button type="button" onClick={openAddNews} className="btn-add-item">
          {t('admin.addNews') || 'Add news item'}
        </button>
        <div className="landing-content-list">
          {news.length === 0 && editingNews === null && (
            <p className="no-items">{t('admin.noNewsItems') || 'No news items. Add one above.'}</p>
          )}
          {news.map((item, index) => (
            <div key={index} className="landing-content-list-item">
              {editingNews === index ? (
                <div className="landing-edit-form">
                  <input
                    type="text"
                    value={newsForm.title}
                    onChange={e => setNewsForm(f => ({ ...f, title: e.target.value }))}
                    placeholder={t('admin.newsTitle') || 'Title'}
                  />
                  <textarea
                    value={newsForm.body}
                    onChange={e => setNewsForm(f => ({ ...f, body: e.target.value }))}
                    placeholder={t('admin.newsBody') || 'Body'}
                    rows={3}
                  />
                  <input
                    type="date"
                    value={newsForm.date}
                    onChange={e => setNewsForm(f => ({ ...f, date: e.target.value }))}
                  />
                  <div className="form-actions">
                    <button type="button" onClick={saveNewsItem} className="btn-primary-sm">{t('admin.save')}</button>
                    <button type="button" onClick={() => setEditingNews(null)} className="btn-secondary-sm">{t('admin.cancel')}</button>
                    <button type="button" onClick={() => deleteNews(index)} className="btn-delete-sm">{t('admin.delete')}</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="item-preview">
                    <strong>{item.title}</strong>
                    <span className="item-date">{item.date ? new Date(item.date).toLocaleDateString() : ''}</span>
                  </div>
                  <div className="item-actions">
                    <button type="button" onClick={() => moveNews(index, -1)} disabled={index === 0} title={t('admin.moveUp') || 'Move up'}>↑</button>
                    <button type="button" onClick={() => moveNews(index, 1)} disabled={index === news.length - 1} title={t('admin.moveDown') || 'Move down'}>↓</button>
                    <button type="button" onClick={() => openEditNews(item, index)} className="btn-edit">{t('admin.edit')}</button>
                    <button type="button" onClick={() => deleteNews(index)} className="btn-delete">{t('admin.delete')}</button>
                  </div>
                </>
              )}
            </div>
          ))}
          {editingNews === 'new' && (
            <div className="landing-content-list-item landing-edit-form-wrapper">
              <div className="landing-edit-form">
                <input
                  type="text"
                  value={newsForm.title}
                  onChange={e => setNewsForm(f => ({ ...f, title: e.target.value }))}
                  placeholder={t('admin.newsTitle') || 'Title'}
                />
                <textarea
                  value={newsForm.body}
                  onChange={e => setNewsForm(f => ({ ...f, body: e.target.value }))}
                  placeholder={t('admin.newsBody') || 'Body'}
                  rows={3}
                />
                <input
                  type="date"
                  value={newsForm.date}
                  onChange={e => setNewsForm(f => ({ ...f, date: e.target.value }))}
                />
                <div className="form-actions">
                  <button type="button" onClick={saveNewsItem} className="btn-primary-sm">{t('admin.save')}</button>
                  <button type="button" onClick={() => setEditingNews(null)} className="btn-secondary-sm">{t('admin.cancel')}</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Advertisements Section */}
      <section className="landing-content-section">
        <h3>{t('landing.advertisements') || 'Advertisements'}</h3>
        <button type="button" onClick={openAddAd} className="btn-add-item">
          {t('admin.addAdvertisement') || 'Add advertisement'}
        </button>
        <div className="landing-content-list ads-list">
          {advertisements.length === 0 && editingAd === null && (
            <p className="no-items">{t('admin.noAdvertisements') || 'No advertisements. Add one above.'}</p>
          )}
          {advertisements.map((item, index) => (
            <div key={index} className="landing-content-list-item ad-item">
              {editingAd === index ? (
                <div className="landing-edit-form">
                  <input
                    type="text"
                    value={adForm.title}
                    onChange={e => setAdForm(f => ({ ...f, title: e.target.value }))}
                    placeholder={t('admin.adTitle') || 'Title / caption'}
                  />
                  <div className="ad-form-image-field">
                    <label className="ad-form-label">{t('admin.adImage') || 'Image'}</label>
                    <MediaSelector
                      currentValue={adForm.image}
                      onSelect={(url) => setAdForm(f => ({ ...f, image: url || '' }))}
                      category="banner"
                    />
                    <input
                      type="text"
                      value={adForm.image}
                      onChange={e => setAdForm(f => ({ ...f, image: e.target.value }))}
                      placeholder={t('admin.adImageUrlOrSelect') || 'Or paste image URL'}
                      className="ad-form-image-url-input"
                    />
                  </div>
                  <input
                    type="text"
                    value={adForm.link}
                    onChange={e => setAdForm(f => ({ ...f, link: e.target.value }))}
                    placeholder={t('admin.adLink') || 'Link (optional)'}
                  />
                  <div className="form-actions">
                    <button type="button" onClick={saveAdItem} className="btn-primary-sm">{t('admin.save')}</button>
                    <button type="button" onClick={() => setEditingAd(null)} className="btn-secondary-sm">{t('admin.cancel')}</button>
                    <button type="button" onClick={() => deleteAd(index)} className="btn-delete-sm">{t('admin.delete')}</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="ad-preview">
                    {item.image && (
                      <img src={getFullImageUrl(item.image)} alt={item.title} className="ad-preview-img" onError={e => { e.target.style.display = 'none'; }} />
                    )}
                    <div className="ad-preview-caption">{item.title}</div>
                  </div>
                  <div className="item-actions">
                    <button type="button" onClick={() => moveAd(index, -1)} disabled={index === 0}>↑</button>
                    <button type="button" onClick={() => moveAd(index, 1)} disabled={index === advertisements.length - 1}>↓</button>
                    <button type="button" onClick={() => openEditAd(item, index)} className="btn-edit">{t('admin.edit')}</button>
                    <button type="button" onClick={() => deleteAd(index)} className="btn-delete">{t('admin.delete')}</button>
                  </div>
                </>
              )}
            </div>
          ))}
          {editingAd === 'new' && (
            <div key="new-ad-form" ref={addAdFormRef} className="landing-content-list-item landing-edit-form-wrapper">
              <div className="landing-edit-form">
                <input
                  type="text"
                  value={adForm.title}
                  onChange={e => setAdForm(f => ({ ...f, title: e.target.value }))}
                  placeholder={t('admin.adTitle') || 'Title / caption'}
                />
                <div className="ad-form-image-field">
                  <label className="ad-form-label">{t('admin.adImage') || 'Image'}</label>
                  <MediaSelector
                    currentValue={adForm.image}
                    onSelect={(url) => setAdForm(f => ({ ...f, image: url || '' }))}
                    category="banner"
                  />
                  <input
                    type="text"
                    value={adForm.image}
                    onChange={e => setAdForm(f => ({ ...f, image: e.target.value }))}
                    placeholder={t('admin.adImageUrlOrSelect') || 'Or paste image URL'}
                    className="ad-form-image-url-input"
                  />
                </div>
                <input
                  type="text"
                  value={adForm.link}
                  onChange={e => setAdForm(f => ({ ...f, link: e.target.value }))}
                  placeholder={t('admin.adLink') || 'Link (optional)'}
                />
                <div className="form-actions">
                  <button type="button" onClick={saveAdItem} className="btn-primary-sm">{t('admin.save')}</button>
                  <button type="button" onClick={() => setEditingAd(null)} className="btn-secondary-sm">{t('admin.cancel')}</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default LandingContent;
